<main
  class="job-details-page"
  [class.is-rtl]="isAr"
  [attr.dir]="isAr ? 'rtl' : 'ltr'"
>
  <ng-container *ngIf="contentReady; else pageLoader">
    <section class="job-details-hero" *ngIf="job; else notFound">
      <div class="job-details-hero-bg"></div>

      <div class="atw-container">
        <button type="button" class="back-btn" (click)="goBack()">
          <span>{{ isAr ? '→' : '←' }}</span>
          {{ t('backToJobs') }}
        </button>

        <div class="job-details-hero-card">
          <div class="job-title-area">
            <small class="status-pill">
              <span>
                <img class="img-s" src="assets/as/27.webp" alt="status" />
              </span>
              {{ getStatusLabel(job.status) }}
            </small>

            <h1>{{ getJobTitle(job) }}</h1>

            <div class="company-line">
              <span>
                <img class="img-m" src="assets/as/9.webp" alt="company" />
                {{ getCompanyName(job) }}
              </span>

              <span>
                <img class="img-m" src="assets/as/21.webp" alt="location" />
                {{ getCityName(job) }}, {{ getCountryName(job) }}
              </span>
            </div>
          </div>

          <div class="salary-card">
            <div class="salary-icon">
              <img class="img-m" src="assets/as/4.webp" alt="salary" />
            </div>

            <div>
              <span>{{ t('salary') }}</span>
              <strong>{{ getSalary(job) }}</strong>
            </div>
          </div>
        </div>
      </div>
    </section>

    <ng-template #notFound>
      <section class="atw-container">
        <div class="empty-job">
          <h2>{{ t('jobNotFound') }}</h2>
          <p>{{ errorMessage || t('jobNotFoundText') }}</p>

          <button type="button" class="primary-action" (click)="goToJobs()">
            {{ t('backToJobs') }}
          </button>
        </div>
      </section>
    </ng-template>

    <section class="atw-container job-details-content" *ngIf="job">
      <aside class="job-apply-card" *ngIf="!isCompanyUser()">
        <div class="apply-card-inner">
          <span class="apply-eyebrow">ATW</span>

          <h2>{{ t('applyTitle') }}</h2>

          <p>
            {{ getCompanyName(job) }}
            <br />
            {{ getCityName(job) }}, {{ getCountryName(job) }}
          </p>

          <button
            type="button"
            class="primary-action"
            (click)="openApplyModal()"
            [disabled]="directApplyingJobId === job?.id || isJobApplied(job)"
          >

            {{ getApplyButtonLabel() }}
          </button>

          <p
            class="apply-feedback"
            *ngIf="applyMessage"
            [class.is-error]="applyMessageType === 'error'"
          >
            {{ applyMessage }}
          </p>
        </div>
      </aside>

      <section class="job-main-details">
        <div class="job-details-grid">
          <div class="detail-card">
            <i>
              <img class="img-m" src="assets/as/4.webp" alt="employment type" />
            </i>
            <span>{{ t('employmentType') }}</span>
            <strong>{{ getJobType(job) }}</strong>
          </div>

          <div class="detail-card">
            <i>
              <img class="img-m" src="assets/as/1.webp" alt="work model" />
            </i>
            <span>{{ t('workModel') }}</span>
            <strong>{{ getWorkModel(job) }}</strong>
          </div>

          <div class="detail-card">
            <i>
              <img class="img-m" src="assets/as/34.webp" alt="posted at" />
            </i>
            <span>{{ t('postedAt') }}</span>
            <strong>{{ getPostedAt(job) }}</strong>
          </div>

          <div class="detail-card">
            <i>
              <img class="img-m" src="assets/as/19.webp" alt="views" />
            </i>
            <span>{{ t('views') }}</span>
            <strong>{{ formatNumber(job.views_count || 0) }}</strong>
          </div>

          <div class="detail-card" [class.active-warning]="job.exam_required">
            <i>
              <img class="img-m" src="assets/as/17.webp" alt="exam required" />
            </i>
            <span>{{ t('examRequired') }}</span>
            <strong>{{ job.exam_required ? t('yes') : t('no') }}</strong>
          </div>

          <div
            class="detail-card"
            [class.active-warning]="job.personality_assessment_required"
          >
            <i>
              <img class="img-m" src="assets/as/26.webp" alt="assessment" />
            </i>
            <span>{{ t('personalityAssessment') }}</span>
            <strong>
              {{ job.personality_assessment_required ? t('yes') : t('no') }}
            </strong>
          </div>
        </div>

        <div class="job-section" *ngIf="getJobDescription(job)">
          <h3>
            <span>
              <img class="img-m" src="assets/as/12.webp" alt="description" />
            </span>
            {{ t('description') }}
          </h3>

          <p>{{ getJobDescription(job) }}</p>
        </div>

        <div class="job-section" *ngIf="getJobResponsibilities(job)">
          <h3>
            <span>
              <img class="img-m" src="assets/as/27.webp" alt="responsibilities" />
            </span>
            {{ t('responsibilities') }}
          </h3>

          <p>{{ getJobResponsibilities(job) }}</p>
        </div>

        <div class="job-section" *ngIf="getTags(job).length">
          <h3>
            <span>
              <img class="img-m" src="assets/as/8.png" alt="skills" />
            </span>
            {{ t('skills') }}
          </h3>

          <div class="job-keywords">
            <small *ngFor="let tag of getTags(job)">
              {{ tag }}
            </small>
          </div>
        </div>

        <div class="job-section" *ngIf="getRequirements(job).length">
          <h3>
            <span>
              <img class="img-m" src="assets/as/35.webp" alt="requirements" />
            </span>
            {{ t('requirements') }}
          </h3>

          <ul class="requirements-list">
            <li
              *ngFor="
                let req of getRequirements(job);
                trackBy: trackByRequirementId
              "
            >
              <span class="req-icon">•</span>

              <div>
                <strong>
                  {{ getRequirementTypeLabel(req.requirement_type) }}:
                </strong>

                {{ getRequirementText(req) }}

                <small *ngIf="req.is_required">
                  {{ t('required') }}
                </small>
              </div>
            </li>
          </ul>
        </div>
      </section>
    </section>
  </ng-container>

  <ng-template #pageLoader>
    <div class="atw-container">
      <div class="jobs-loading page-loader">
        <span></span>
        <p>{{ t('loading') }}</p>
      </div>
    </div>
  </ng-template>
</main>

<div
  class="apply-modal-backdrop"
  *ngIf="applyModalOpen && !isCompanyUser()"
  (click)="closeApplyModal()"
>
  <div class="apply-modal" (click)="$event.stopPropagation()">
    <button
      type="button"
      class="apply-modal-close"
      (click)="closeApplyModal()"
    >
      ×
    </button>

    <div class="apply-modal-head" *ngIf="job">
      <span>{{ t('applyNow') }}</span>

      <h2>{{ getJobTitle(job) }}</h2>

      <p>
        {{ getCompanyName(job) }}
        • {{ getCityName(job) }},
        {{ getCountryName(job) }}
      </p>
    </div>

    <div class="apply-modal-body">
      <app-atw-cv-uploader
        *ngIf="getApplicationFormType(job) === 'general'"
        [jobId]="job?.id"
        (applicationSubmitted)="handleApplicationSubmitted()"
      ></app-atw-cv-uploader>

      <app-applyfordoctor
        *ngIf="getApplicationFormType(job) === 'doctor'"
        [jobId]="job?.id"
        (applicationSubmitted)="handleApplicationSubmitted()"
      ></app-applyfordoctor>

      <app-applyfordentist
        *ngIf="getApplicationFormType(job) === 'dentist'"
        [jobId]="job?.id"
        (applicationSubmitted)="handleApplicationSubmitted()"
      ></app-applyfordentist>
    </div>
  </div>
</div>
